import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fetchLeaderboard } from "../../features/store/userSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { leaderboardData } from "../../data/DemoData";
import { RootState } from "../../redux/store";

const Leaderboard = () => {
  const [toggle, setToggle] = useState<string>("monthly");
  const [activeIndex, setActiveIndex] = useState(0);
  const dispatch = useAppDispatch();
  const { leaderboard, loading } = useAppSelector(
    (state: RootState) => state.users,
  );

  const rawData = leaderboard || [];

  // Grouping for Mobile: 2 cards per slide
  const mobilePairs = [];
  for (let i = 0; i < rawData.length; i += 2) {
    mobilePairs.push(rawData.slice(i, i + 2));
  }

  useEffect(() => {
    dispatch(fetchLeaderboard(toggle));
  }, [toggle, dispatch]);

  // Auto-slide logic for mobile only
  useEffect(() => {
    if (mobilePairs.length <= 1) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % mobilePairs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [mobilePairs.length]);

  return (
    <div
      className=" flex flex-col gap-4 p-3 bp:p-5 w-full col-4 rounded-lg bp:min-w-[288px]"
      style={{
        backgroundImage: `url(/images/bg.png)`,
        // backgroundPosition: "center top", // Focus on the top-center part of the image
        // backgroundSize: "400% 300%", // Stretch the image height to 200% so the top 50% is visible
        backgroundRepeat: "no-repeat",
      }}
    >
      <Header toggle={toggle} setToggle={setToggle} />
      <div className="hidden bp:flex">
        <Body data={leaderboard || []} loading={loading} type={toggle} />
      </div>

      <div className="bp:hidden overflow-hidden">
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {mobilePairs.map((pair, slideIdx) => (
            <div key={slideIdx} className="min-w-full flex flex-row gap-2">
              {pair.map((user, idx) => (
                <div key={idx} className="flex-1 min-w-0">
                  <MobileCard
                    user={user}
                    index={rawData.indexOf(user)}
                    type={toggle}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Dots for Mobile */}
        <div className="flex justify-center gap-2 mt-4">
          {mobilePairs.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`h-1 rounded-full transition-all ${
                activeIndex === i ? "bg-white w-8" : "bg-white/30 w-3"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;

const MobileCard = ({ user, index, type }: any) => {
  const { t } = useTranslation();
  const translate = t as unknown as (key: string) => string;
  return (
    <div className="flex flex-row items-center gap-2 rounded-lg p-2 bg-white dark:bg-BG-dark shadow-sm w-full">
      <div className="shrink-0">
        <Position index={index} />
      </div>
      <img
        className="size-8 rounded-full object-cover shrink-0" // Reduced size from 11 to 8 for mobile fit
        src={user.image_url || "/png/IVCartoon.png"}
        alt="user"
      />
      <div className="flex flex-col justify-center min-w-0">
        {" "}
        <span className="text-black dark:text-main-dark text-[12px] font-bold truncate leading-tight">
          {user.username || user.firstname}
        </span>
        <span className="text-[#29aaef] text-[11px] font-bold leading-tight">
          {type === "monthly"
            ? user.monthly_accumulated_points
            : user.alltime_accumulated_points}{" "}
          {translate("main.points")}
        </span>
      </div>
    </div>
  );
};

const Header = ({
  toggle,
  setToggle,
}: {
  toggle: string;
  setToggle: (val: string) => void;
}) => {
  const { t } = useTranslation();
  const translate = t as unknown as (key: string) => string;

  return (
    <div className="col-4">
      <div className="col-2 place-items-center">
        <img src="/png/crown.png" alt="" className="size-10" />
        <span className="lt-xl-semibold-main-dark">
          {translate("main.leaderboard")}
        </span>
      </div>
      <div className="rounded-2xl p-[6px] row-2 bg-BG-light dark:bg-BG-dark">
        {["monthly", "allTime"].map((item, index) => (
          <button
            key={index}
            className={`flex-1 py-[6px] px-[10px] place-content-center ${
              toggle === item
                ? "inter-xs-semibold-white rounded-2xl bg-toggleBG-light"
                : "inter-xs-medium-toggleBG-dark dark:text-main-dark"
            }`}
            onClick={() => setToggle(item)}
          >
            {translate(`main.${item}`)}
          </button>
        ))}
      </div>
    </div>
  );
};

const Body = ({
  data,
  loading,
  type,
}: {
  data: any[];
  loading: boolean;
  type: string;
}) => {
  leaderboardData.sort((a, b) => b.point - a.point);
  const { t } = useTranslation();
  const translate = t as unknown as (key: string) => string;
  return (
    <div className="flex flex-col gap-2 w-full">
      {data.map((user, index) => (
        <div className="flex flex-row items-center gap-4 rounded-lg px-4 py-2 bg-BG-light dark:bg-BG-dark w-full shadow-sm">
          <div className="row-2 items-center">
            <Position index={index} />
            <img
              className="size-11 rounded-full object-cover"
              src={user.image_url || "/png/IVCartoon.png"}
              alt={user.username}
            />
          </div>
          <div className="flex flex-col justify-center">
            <span className="inter-xs-bold-black dark:text-main-dark">
              {/* {leaderboard.title} */}
              {user.username || `${user.firstname}`}
            </span>
            <span className="inter-xs-bold-pry-600 text-[10px]">
              {type === "monthly"
                ? user.monthly_accumulated_points
                : user.alltime_accumulated_points}{" "}
              {translate("main.points")}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

const Position = ({ index }: { index: number }) => {
  const commonClassses =
    "size-6 flex items-center justify-center rounded-full p-2";
  if (index < 3) {
    const lbColor = ["#FFBF00", "#C0C0C0", "#CD7F32"];
    const bgColor = lbColor[index];
    return (
      <span
        className={`${commonClassses} satoshi-sm-bold-white`}
        style={{ backgroundColor: bgColor }}
      >
        {index + 1}
      </span>
    );
  } else {
    return (
      <span
        className={`${commonClassses} satoshi-sm-bold-toggleBG-dark dark:text-white border border-border-light dark:border-x-border-dark`}
      >
        {index + 1}
      </span>
    );
  }
};
